import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, Mail, Phone, MapPin, Calendar, ChevronDown, Facebook, Instagram, ShoppingBag } from "lucide-react";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CartDrawer } from "@/components/CartDrawer";
import { useCategoryOrder, sortBySlug } from "@/hooks/useCategoryOrder";
import { useCart } from "@/context/CartContext";
import { FEATURE_FLAGS } from "@/config/featureFlags";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/bushtech", label: "Ute Canopies & Hard Lids" },
  { to: "/clearance", label: "Clearance" },
  { to: "/expeditions", label: "XpeditioNZ Experience" },
  { to: "/about", label: "About" },
];

// Shared across the "Canopies & Hard Lids" dropdown (pinned first, below)
// and the "Shop" dropdown — Hard Lids is a genuine aluminium Bushtech-family
// accessory as well as a general shop item, so it's deliberately cross-listed
// in both (same pattern already used for Bushtech/Clearance appearing in
// both the top nav and the Shop dropdown).
const HARD_LIDS_SUBLINK = { to: "/hard-lids", label: "Hard Lids", note: "Tour Top Cover — lockable lid & roof rack in one, coming soon" };

const BUSHTECH_SUBLINKS = [
  { to: "/bushtech", label: "Full Range Overview", note: "Aluminium hard lids and canopies through to ute camper" },
  { to: "/wellside-canopies", label: "Bushtech Aluminium Canopies", note: "Premium welded canopies for factory ute tubs" },
  { to: "/x-series-tray", label: "X-Series Tray & Canopy", note: "Expedition grade tray & canopy" },
  { to: "/bush-cabin", label: "Bush Cabin Ute Camper", note: "Live-in comfort, 4WD capability" },
];

// Rendered as an indented callout directly under "Wellside Canopies" in the
// dropdown (not part of the reorderable list above) — makes it clear these
// accessories are specifically for Wellside canopies, not a standalone
// product line of their own.
const WELLSIDE_ACCESSORIES_SUBLINK = { to: "/wellside-accessories", label: "Canopy Accessories", note: "Locking, dust sealing, racks & storage" };

const SHOP_SUBLINKS = [
  { to: "/roof-tents", label: "Roof Tents", note: "Hard shell & soft shell rooftop tents" },
  { to: "/ground-tents", label: "Ground Tents", note: "Tentco safari canvas ground tents" },
  { to: "/awnings", label: "Awnings", note: "Quick Pitch 270° & side awnings" },
  { to: "/roof-racks", label: "Roof Racks", note: "K9 platform racks & load bars" },
  ...(FEATURE_FLAGS.HARD_LIDS_ENABLED ? [HARD_LIDS_SUBLINK] : []),
  BUSHTECH_SUBLINKS[3],
  { to: "/bull-bars", label: "Bull Bars, Rear Bars, Sliders & More", note: "Front bars, rear bars & rock sliders" },
  { to: "/drawers-kitchens-tables", label: "Drawers, Kitchens & Tables", note: "Fridge slides, storage systems, premium designs" },
  { to: "/12v-fridges", label: "12V Fridges", note: "National Luna commercial-grade fridges" },
  { to: "/12v-fridges#power-lighting", label: "LED Lighting", note: "Power packs & National Luna LED lights" },
  { to: "/camp-cooking", label: "Camp & Portable Cooking", note: "CADAC gas BBQs & portable cooking gear" },
  { to: "/camping-accessories", label: "Unique Camping Gear", note: "Ammo boxes, ensuite floors & essentials" },
  { to: "/ensuites", label: "Ensuites & Related Products", note: "Quick Pitch & EEZI-AWN shower tents, brackets" },
  { to: "/jimny-store", label: "Suzuki Jimny Store", note: "Dedicated kit built for the Jimny" },
  { to: "/clearance", label: "Clearance & Specials", note: "Ex-display units, limited-time savings" },
];

const BUSHTECH_PATH_PREFIXES = ["/bushtech", "/wellside-canopies", "/wellside-accessories", "/x-series-tray", "/bush-cabin"];
const isBushtechPage = (pathname) => BUSHTECH_PATH_PREFIXES.some((p) => pathname.startsWith(p));
const isShopPage = (pathname) => SHOP_SUBLINKS.some((link) => pathname.startsWith(link.to));
const slugFromPath = (to) => to.replace(/^\//, "").split("#")[0];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const bushtechPage = isBushtechPage(location.pathname);
  const categoryOrder = useCategoryOrder();
  const { totalCount, setIsOpen: setCartOpen } = useCart();
  // "Full Range Overview" is pinned first (entry point to the whole range),
  // then Hard Lids — neither is a reorderable product category, so both sit
  // ahead of the sorted Wellside/X-Series/Bush Cabin sublinks.
  const bushtechSublinks = [
    BUSHTECH_SUBLINKS[0],
    ...(FEATURE_FLAGS.HARD_LIDS_ENABLED ? [HARD_LIDS_SUBLINK] : []),
    ...sortBySlug(BUSHTECH_SUBLINKS.slice(1), categoryOrder, (l) => slugFromPath(l.to)),
  ];
  const shopSublinks = [
    { to: "/bushtech", label: "Full Canopy Range", note: "Full range overview — canopies, trays & campers" },
    ...sortBySlug(SHOP_SUBLINKS, categoryOrder, (l) => slugFromPath(l.to)),
  ];

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-header" : "bg-gradient-to-b from-black/70 via-black/40 to-transparent"}`}
    >
      <div className="container-x flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0">
          {bushtechPage ? (
            <img
              src="/logos/bushtech-logo.png"
              alt="Bushtech Aluminium Canopies"
              data-testid="header-logo-bushtech"
              className="h-8 sm:h-10 md:h-12 w-auto"
            />
          ) : (
            <img
              src="/logos/xpeditionz-logo.png"
              alt="XpeditioNZ"
              data-testid="header-logo-xpeditionz"
              className="h-7 sm:h-8 md:h-10 w-auto"
            />
          )}
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-10">
        <nav className="flex items-center gap-2 xl:gap-3">
          {NAV.map((item) => {
            if (item.to === "/bushtech") {
              return (
                <div key="bushtech-and-shop" style={{ display: "contents" }}>
                  <div className="relative group flex items-center" data-testid="nav-bushtech-wrapper">
                    <NavLink
                      to={item.to}
                      data-testid="nav-link-bushtech"
                      className={({ isActive }) =>
                        `px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] whitespace-nowrap transition-colors ${
                          isActive ? "text-[var(--brand-primary)]" : "text-white/70 hover:text-white"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                    <div
                      data-testid="nav-bushtech-dropdown"
                      className="absolute left-0 top-full pt-3 opacity-0 invisible -translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 min-w-[280px]"
                    >
                      <div className="bg-[var(--bg-secondary)] border border-[var(--border-default)] shadow-2xl">
                        {bushtechSublinks.flatMap((link, i) => {
                          const items = [
                            <Link
                              key={link.to}
                              to={link.to}
                              data-testid={`nav-bushtech-sublink-${i}`}
                              className="block px-5 py-4 border-b border-[var(--border-default)] last:border-0 hover:bg-white/5 transition-colors group/item"
                            >
                              <span className="block text-xs font-mono uppercase tracking-[0.18em] text-white group-hover/item:text-[var(--brand-primary)] transition-colors">
                                {link.label}
                              </span>
                              <span className="block text-sm text-[var(--text-tertiary)] mt-1">{link.note}</span>
                            </Link>,
                          ];
                          if (link.to === "/wellside-canopies") {
                            items.push(
                              <Link
                                key="wellside-accessories-nested"
                                to={WELLSIDE_ACCESSORIES_SUBLINK.to}
                                data-testid="nav-bushtech-sublink-wellside-accessories"
                                className="block pl-9 pr-5 py-3 border-b border-[var(--border-default)] last:border-0 bg-black/20 hover:bg-white/5 transition-colors group/item"
                              >
                                <span className="block text-xs font-mono uppercase tracking-[0.16em] text-white/70 group-hover/item:text-[var(--brand-primary)] transition-colors">
                                  — {WELLSIDE_ACCESSORIES_SUBLINK.label}
                                </span>
                                <span className="block text-sm text-[var(--text-tertiary)] mt-1">{WELLSIDE_ACCESSORIES_SUBLINK.note}</span>
                              </Link>
                            );
                          }
                          return items;
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="relative group" data-testid="nav-shop-wrapper">
                    <button
                      type="button"
                      data-testid="nav-shop-trigger"
                      className={`flex items-center gap-1 px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] transition-colors ${
                        isShopPage(location.pathname) ? "text-[var(--brand-primary)]" : "text-white/70 hover:text-white"
                      }`}
                    >
                      Shop <ChevronDown size={12} className="group-hover:rotate-180 transition-transform duration-200" />
                    </button>
                    <div
                      data-testid="nav-shop-dropdown"
                      style={{ right: "max(48px, calc((100vw - 1440px) / 2 + 48px))" }}
                      className="fixed top-20 pt-3 opacity-0 invisible -translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200 w-[min(720px,calc(100vw-2.5rem))]"
                    >
                      <div className="grid grid-cols-3 bg-[var(--bg-secondary)] border border-[var(--border-default)] shadow-2xl">
                        {shopSublinks.map((link, i) => {
                          const Tag = link.external ? "a" : Link;
                          const tagProps = link.external ? { href: link.to, target: "_blank", rel: "noopener noreferrer" } : { to: link.to };
                          return (
                            <Tag
                              key={link.to}
                              {...tagProps}
                              data-testid={`nav-shop-sublink-${i}`}
                              className="block px-5 py-4 border-b border-r border-[var(--border-default)] [&:nth-child(3n)]:border-r-0 hover:bg-white/5 transition-colors group/item"
                            >
                              <span className="block text-xs font-mono uppercase tracking-[0.18em] text-white group-hover/item:text-[var(--brand-primary)] transition-colors">
                                {link.label}
                              </span>
                              <span className="block text-[11px] text-[var(--text-tertiary)] mt-1">{link.note}</span>
                            </Tag>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] whitespace-nowrap transition-colors ${
                    isActive ? "text-[var(--brand-primary)]" : "text-white/70 hover:text-white"
                  }`
                }
                end={item.to === "/"}
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <a href="#enquire" data-testid="header-cta-enquire" className="btn-primary text-xs shrink-0">
          Enquire <ArrowUpRight size={14} />
        </a>
        <button
          type="button"
          aria-label="Open cart"
          data-testid="header-cart-button"
          onClick={() => setCartOpen(true)}
          className="relative text-white/80 hover:text-white transition-colors shrink-0"
        >
          <ShoppingBag size={22} />
          {totalCount > 0 && (
            <span data-testid="header-cart-count" className="absolute -top-2 -right-2 bg-[var(--brand-primary)] text-black text-[10px] font-mono font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {totalCount}
            </span>
          )}
        </button>
        </div>

        <button
          type="button"
          aria-label="Open cart"
          data-testid="mobile-cart-button"
          onClick={() => setCartOpen(true)}
          className="lg:hidden relative text-white p-2"
        >
          <ShoppingBag size={22} />
          {totalCount > 0 && (
            <span data-testid="mobile-cart-count" className="absolute top-0 right-0 bg-[var(--brand-primary)] text-black text-[10px] font-mono font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {totalCount}
            </span>
          )}
        </button>
        <button
          aria-label="menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="lg:hidden text-white p-2"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden glass-header border-t border-white/10 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="container-x py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <div key={item.to}>
                <NavLink
                  to={item.to}
                  data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/&/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "")}`}
                  className={({ isActive }) =>
                    `py-3 text-sm font-mono uppercase tracking-[0.18em] border-b border-white/5 block ${
                      isActive ? "text-[var(--brand-primary)]" : "text-white/80"
                    }`
                  }
                  end={item.to === "/"}
                >
                  {item.label}
                </NavLink>
                {item.to === "/bushtech" && (
                  <>
                    <div className="pl-4 flex flex-col" data-testid="mobile-nav-bushtech-sublinks">
                      {bushtechSublinks.flatMap((link, i) => {
                        const items = [
                          <Link
                            key={link.to}
                            to={link.to}
                            data-testid={`mobile-nav-bushtech-sublink-${i}`}
                            className="py-3 text-xs font-mono uppercase tracking-[0.15em] text-white/60 border-b border-white/5"
                          >
                            {link.label}
                          </Link>,
                        ];
                        if (link.to === "/wellside-canopies") {
                          items.push(
                            <Link
                              key="wellside-accessories-nested"
                              to={WELLSIDE_ACCESSORIES_SUBLINK.to}
                              data-testid="mobile-nav-bushtech-sublink-wellside-accessories"
                              className="pl-4 py-3 text-[11px] font-mono uppercase tracking-[0.15em] text-white/45 border-b border-white/5"
                            >
                              — {WELLSIDE_ACCESSORIES_SUBLINK.label}
                            </Link>
                          );
                        }
                        return items;
                      })}
                    </div>
                    <div className="py-3 text-sm font-mono uppercase tracking-[0.18em] text-white/80 border-b border-white/5" data-testid="mobile-nav-shop-label">
                      Shop
                    </div>
                    <div className="pl-4 flex flex-col" data-testid="mobile-nav-shop-sublinks">
                      {shopSublinks.map((link, i) => {
                        const Tag = link.external ? "a" : Link;
                        const tagProps = link.external ? { href: link.to, target: "_blank", rel: "noopener noreferrer" } : { to: link.to };
                        return (
                          <Tag
                            key={link.to}
                            {...tagProps}
                            data-testid={`mobile-nav-shop-sublink-${i}`}
                            className="py-3 text-xs font-mono uppercase tracking-[0.15em] text-white/60 border-b border-white/5"
                          >
                            {link.label}
                          </Tag>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            ))}
            <a href="#enquire" data-testid="mobile-cta-enquire" className="btn-primary text-xs mt-4 justify-center">
              Enquire <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      )}
      <CartDrawer />
    </header>
  );
};

export const Footer = () => {
  const { pathname } = useLocation();
  const bushtech = isBushtechPage(pathname);
  // These pages have a fixed bottom sticky CTA bar on mobile (md:hidden) —
  // without extra bottom padding here, that bar sits on top of the legal
  // links below and makes them unclickable at the very bottom of the page.
  const hasMobileStickyBar = ["/wellside-canopies", "/bush-cabin", "/roof-tents", "/hard-lids"].includes(pathname);

  return (
    <footer data-testid="site-footer" className="bg-[var(--bg-secondary)] border-t border-[var(--border-default)] mt-8 md:mt-32">
      <div id="enquire" className="container-x pt-6 pb-24 md:py-24 scroll-mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-6">— Get In Touch</p>
            <h2 className="font-display text-5xl md:text-7xl text-white leading-[0.95] mb-8">
              READY TO<br />
              BUILD YOUR RIG?
            </h2>
            <p className="text-[var(--text-secondary)] max-w-xl text-lg leading-relaxed mb-6">
              {bushtech
                ? "Whether you're outfitting a single ute or a fleet, we'll match the right Bushtech canopy, tray or camper build to your vehicle and use case."
                : "Whether you're kitting out one vehicle or a whole fleet, we'll help you choose the right gear for your build and budget."}
            </p>
            <div data-testid="footer-demo-callout" className="flex items-start gap-3 border border-[var(--brand-primary)]/50 bg-[var(--brand-primary)]/10 px-4 py-4 mb-8">
              <MapPin size={20} className="text-[var(--brand-primary)] mt-0.5 shrink-0" strokeWidth={1.6} />
              <p className="text-sm md:text-base text-white leading-snug">
                {bushtech ? (
                  <>
                    <span className="font-semibold text-[var(--brand-primary)]">Bushtech demonstrator vehicles</span> are available to view in Christchurch — get in touch to arrange a viewing.
                  </>
                ) : (
                  <>
                    <span className="font-semibold text-[var(--brand-primary)]">Based in or passing through Christchurch?</span> Get in touch to arrange an up-close look at the range before you buy.
                  </>
                )}
              </p>
            </div>
            <div className="space-y-3 text-sm text-[var(--text-secondary)]">
              <div className="flex items-start gap-3"><Mail size={14} className="mt-1 text-[var(--brand-primary)]" /><a href="mailto:support@xpeditionz.co.nz" className="hover:text-white transition-colors">support@xpeditionz.co.nz</a></div>
              <div className="flex items-start gap-3"><Phone size={14} className="mt-1 text-[var(--brand-primary)]" /><a href="tel:+64284349101" className="hover:text-white transition-colors">028 434 9101</a></div>
              <div className="flex items-start gap-3"><MapPin size={14} className="mt-1 text-[var(--brand-primary)]" /><span>Christchurch, NZ</span></div>
              <div className="flex items-start gap-3"><Calendar size={14} className="mt-1 text-[var(--brand-primary)]" /><span>{bushtech ? "Demo vehicles by appointment" : "Product viewings by appointment"}</span></div>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="https://photos.app.goo.gl/FAy62ZWh6aVKq34D7" target="_blank" rel="noopener noreferrer" data-testid="footer-gallery" className="btn-secondary">
                View NZ Customer Gallery <ArrowUpRight size={14} />
              </a>
              <div className="flex items-center gap-3" data-testid="footer-social-links">
                <a
                  href="https://www.facebook.com/Xpeditionz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="XpeditioNZ on Facebook"
                  data-testid="footer-social-facebook"
                  className="w-10 h-10 flex items-center justify-center border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--brand-primary)] hover:border-[var(--brand-primary)] transition-colors"
                >
                  <Facebook size={16} />
                </a>
                <a
                  href="https://www.instagram.com/xpeditionz.co.nz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="XpeditioNZ on Instagram"
                  data-testid="footer-social-instagram"
                  className="w-10 h-10 flex items-center justify-center border border-[var(--border-default)] text-[var(--text-secondary)] hover:text-[var(--brand-primary)] hover:border-[var(--brand-primary)] transition-colors"
                >
                  <Instagram size={16} />
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-10">
              <p className="overline mb-2">— Send An Enquiry</p>
              <p className="text-white text-sm mb-6">Trade, fleet & dealer enquiries welcome.</p>
              <EnquiryForm idPrefix="footer-enquiry" />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--border-default)]">
        <div className={`container-x py-6 flex flex-col md:flex-row items-center justify-between gap-4 ${hasMobileStickyBar ? "pb-20 md:pb-6" : ""}`}>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--text-tertiary)]">
            © {new Date().getFullYear()} XpeditioNZ — Bushtech NZ Distributor
          </p>
          <div className="flex items-center gap-6" data-testid="footer-legal-links">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-tertiary)] hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/shipping-policy" data-testid="footer-shipping-link" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-tertiary)] hover:text-white transition-colors">
              Shipping Policy
            </Link>
            <Link to="/returns-policy" data-testid="footer-returns-link" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[var(--text-tertiary)] hover:text-white transition-colors">
              Returns & Warranty Policy
            </Link>
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[var(--text-tertiary)]">
            Built in South Africa · Proven Globally
          </p>
        </div>
      </div>
    </footer>
  );
};
